import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame } from "remotion";
import { COLORS, FONTS, SPRING, TOTAL_SLIDES } from "./constants";

/* ───────────────────────── tiny helpers ───────────────────────── */

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function glow(c: string, strong = false): string {
  return strong
    ? `0 0 26px ${c}cc, 0 0 60px ${c}77, 0 0 120px ${c}33`
    : `0 0 16px ${c}99, 0 0 40px ${c}44`;
}

/** Spring-eased 0→1 appear value with an optional frame delay. */
export function appearAt(
  frame: number,
  fps: number,
  delay = 0,
  cfg: { damping: number; mass: number } = SPRING.snappy,
): number {
  return spring({
    frame: frame - delay,
    fps,
    config: cfg,
  });
}

/** Lerp 0→1 over a frame window, clamped. */
export function ramp(frame: number, from: number, to: number): number {
  return interpolate(frame, [from, to], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
}

/** Integer count-up that lands on `target` by `endFrame`. */
export function countUp(frame: number, target: number, endFrame = 34): number {
  const p = interpolate(frame, [0, endFrame], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // ease-out cubic
  const e = 1 - Math.pow(1 - p, 3);
  return Math.round(target * e);
}

/* ───────────────────────── animated backdrop ───────────────────────── */

export const Backdrop: React.FC<{ frame: number; accent: string }> = ({
  frame,
  accent,
}) => {
  const drift = Math.sin(frame * 0.006) * 9;
  const orbs = [
    { x: 250, y: 250, s: 600, c: accent, sp: 0.005, ph: 0 },
    { x: 1690, y: 320, s: 520, c: COLORS.neonPurple, sp: 0.004, ph: 2 },
    { x: 960, y: 900, s: 680, c: COLORS.neonBlue, sp: 0.003, ph: 4 },
    { x: 430, y: 820, s: 420, c: accent, sp: 0.0035, ph: 1.2 },
    { x: 1520, y: 800, s: 460, c: COLORS.neonCyan, sp: 0.0045, ph: 3.1 },
  ];
  const pcol = [
    accent,
    COLORS.neonBlue,
    COLORS.neonCyan,
    COLORS.neonPurple,
    COLORS.neonPink,
    COLORS.neonGreen,
  ];
  return (
    <>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 82% 60% at 50% 42%, ${accent}1c 0%, transparent 70%)`,
        }}
      />
      {/* perspective grid floor */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(${accent}12 1px, transparent 1px), linear-gradient(90deg, ${accent}12 1px, transparent 1px)`,
          backgroundSize: "84px 84px",
          transform: `perspective(900px) rotateX(34deg) translateY(${drift}px) scale(1.6)`,
          transformOrigin: "50% 100%",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, #000 40%, #000 100%)",
        }}
      />
      {/* soft orbs */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {orbs.map((o, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: o.x + Math.sin(frame * o.sp + o.ph) * 46 - o.s / 2,
              top: o.y + Math.cos(frame * o.sp * 0.7 + o.ph) * 32 - o.s / 2,
              width: o.s,
              height: o.s,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${o.c}10 0%, transparent 70%)`,
            }}
          />
        ))}
      </div>
      {/* drifting particles */}
      {Array.from({ length: 24 }).map((_, i) => {
        const px = 80 + ((i * 167) % 1840);
        const py = 50 + ((i * 101) % 1000);
        const c = pcol[i % pcol.length];
        const sz = 3 + (i % 4);
        return (
          <div
            key={`p-${i}`}
            style={{
              position: "absolute",
              left: px + Math.sin(frame * 0.02 + i * 1.3) * 28,
              top: py + Math.cos(frame * 0.015 + i * 1.7) * 22,
              width: sz,
              height: sz,
              borderRadius: "50%",
              background: c,
              boxShadow: `0 0 ${sz * 3}px ${c}cc, 0 0 ${sz * 6}px ${c}55`,
              opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.3,
              pointerEvents: "none",
            }}
          />
        );
      })}
      {/* vignette */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 120% 120% at 50% 50%, transparent 55%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />
    </>
  );
};

/** Full-bleed shell: animated backdrop + content. No exit fade (transitions handle it). */
export const SlideShell: React.FC<{
  accent: string;
  children: React.ReactNode;
}> = ({ accent, children }) => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        color: COLORS.textPrimary,
        overflow: "hidden",
      }}
    >
      <Backdrop frame={frame} accent={accent} />
      {children}
    </AbsoluteFill>
  );
};

/* ───────────────────────── chrome ───────────────────────── */

export const SlideChip: React.FC<{
  n: number;
  color: string;
  appear: number;
  label?: string;
}> = ({ n, color, appear, label }) => (
  <div
    style={{
      position: "absolute",
      top: 46,
      left: 64,
      opacity: appear,
      display: "flex",
      gap: 13,
      alignItems: "center",
      zIndex: 30,
    }}
  >
    <div
      style={{
        padding: "9px 19px",
        borderRadius: 30,
        background: `${color}1c`,
        border: `1.5px solid ${color}aa`,
        fontSize: 17,
        fontFamily: FONTS.mono,
        color,
        fontWeight: 800,
        letterSpacing: 2.4,
        textShadow: glow(color),
      }}
    >
      {pad2(n)} / {pad2(TOTAL_SLIDES)}
    </div>
    {label ? (
      <div
        style={{
          fontSize: 14,
          fontFamily: FONTS.mono,
          color: COLORS.textMuted,
          letterSpacing: 2,
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
    ) : null}
  </div>
);

export const BrandMark: React.FC<{ appear: number }> = ({ appear }) => (
  <div
    style={{
      position: "absolute",
      top: 50,
      right: 64,
      opacity: appear * 0.8,
      fontFamily: FONTS.mono,
      fontSize: 13,
      letterSpacing: 5,
      textTransform: "uppercase",
      color: COLORS.textMuted,
      zIndex: 30,
    }}
  >
    EAKC · a solo build
  </div>
);

export const Pill: React.FC<{
  text: React.ReactNode;
  color: string;
  appear?: number;
  solid?: boolean;
  size?: number;
}> = ({ text, color, appear = 1, solid = false, size = 18 }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      padding: `${size * 0.42}px ${size * 0.95}px`,
      borderRadius: 999,
      fontSize: size,
      fontFamily: FONTS.mono,
      fontWeight: 800,
      letterSpacing: 0.6,
      color: solid ? COLORS.bg : color,
      background: solid ? color : `${color}1e`,
      border: `1.5px solid ${color}`,
      boxShadow: `0 0 18px ${color}55`,
      opacity: appear,
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </div>
);

/** Gradient headline text. */
export const Grad: React.FC<{
  children: React.ReactNode;
  from: string;
  to: string;
}> = ({ children, from, to }) => (
  <span
    style={{
      background: `linear-gradient(100deg, ${from}, ${to})`,
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
    }}
  >
    {children}
  </span>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
